import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconCalendar, IconFileText, IconUpload, IconX } from "@tabler/icons-react"
import { format, parse } from "date-fns"
import { useEffect, useState } from "react"
import type { DateRange } from "react-day-picker"

interface Balance {
  id: number
  balance_type: string
  year: number
  days_available: number
  days_used: number
  remaining_days: number
}

interface Props {
  [key: string]: unknown
  balances: Balance[]
  contract_hours_per_day: number
  notice_days: number
  prefill: {
    request_type?: string
    request_type_unit?: string
    start_date?: string
    end_date?: string
    hours_requested?: number
  }
  auth: {
    is_admin: boolean
  }
}

interface CalcResult {
  business_days: number
  total_hours: number
  holidays: string[]
}

interface OverlappingTeammate {
  user_name: string
  request_type: string
  start_date: string
  end_date: string
  status: string
}

function formatDate(dateStr: string) {
  return format(parse(dateStr, "yyyy-MM-dd", new Date()), "MMM d")
}

function formatDays(value: number | string) {
  const n = Number(value)
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)
}

const REQUEST_TYPES = [
  { value: "holiday", label: "Holiday" },
  { value: "parental", label: "Parental" },
  { value: "study", label: "Study" },
  { value: "unpaid", label: "Unpaid" },
]

function isShortNotice(startDate: string, noticeDays: number): boolean {
  if (!startDate) return false
  const start = parse(startDate, "yyyy-MM-dd", new Date())
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cutoff = new Date(today)
  cutoff.setDate(cutoff.getDate() + noticeDays)
  return start >= today && start < cutoff
}

export default function TimeOffRequestNew() {
  const { balances, contract_hours_per_day, notice_days, prefill, auth } = usePage<Props>().props

  const [data, setFormData] = useState({
    request_type: prefill.request_type || "",
    request_type_unit: prefill.request_type_unit || "days",
    start_date: prefill.start_date || "",
    end_date: prefill.end_date || "",
    hours_requested: prefill.hours_requested || ("" as string | number),
    notes: "",
  })
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [codeDialogOpen, setCodeDialogOpen] = useState(false)
  const [managerCode, setManagerCode] = useState("")

  const shortNotice = !auth.is_admin && isShortNotice(data.start_date, notice_days)

  function setData<K extends keyof typeof data>(key: K, value: (typeof data)[K]) {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const [calculation, setCalculation] = useState<CalcResult | null>(null)
  const [overlapping, setOverlapping] = useState<OverlappingTeammate[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  useEffect(() => {
    if (data.start_date && data.end_date) {
      fetchCalculation()
      fetchOverlapping()
    } else {
      setOverlapping([])
    }
  }, [data.start_date, data.end_date])

  async function fetchCalculation() {
    try {
      const res = await fetch(`/calculate_business_days?start_date=${data.start_date}&end_date=${data.end_date}`)
      if (res.ok) {
        const result = await res.json()
        setCalculation(result)
      }
    } catch {
      // silently fail
    }
  }

  async function fetchOverlapping() {
    try {
      const res = await fetch(`/overlapping_teammates?start_date=${data.start_date}&end_date=${data.end_date}`)
      if (res.ok) {
        setOverlapping(await res.json())
      }
    } catch {
      setOverlapping([])
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (shortNotice) {
      setCodeDialogOpen(true)
      return
    }

    submitRequest()
  }

  function submitRequest(code?: string) {
    setProcessing(true)
    setErrors({})

    const formData = new FormData()
    formData.append("time_off_request[request_type]", data.request_type)
    formData.append("time_off_request[request_type_unit]", data.request_type_unit)
    formData.append("time_off_request[start_date]", data.start_date)
    formData.append("time_off_request[end_date]", data.end_date)
    formData.append("time_off_request[hours_requested]", String(data.hours_requested))
    formData.append("time_off_request[notes]", data.notes)
    if (code) {
      formData.append("time_off_request[manager_code]", code)
    }
    selectedFiles.forEach((file) => {
      formData.append("time_off_request[files][]", file)
    })

    router.post("/time_off_requests", formData as any, {
      forceFormData: true,
      onError: (errs) => {
        setErrors(errs as Record<string, string>)
      },
      onSuccess: () => {
        setCodeDialogOpen(false)
      },
      onFinish: () => {
        setProcessing(false)
      },
    })
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || [])
    setSelectedFiles((prev) => [...prev, ...files])
  }

  function removeFile(index: number) {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    setSelectedFiles((prev) => [...prev, ...files])
  }

  const groupedBalances = balances.reduce<Record<string, Balance[]>>((acc, b) => {
    if (!acc[b.balance_type]) acc[b.balance_type] = []
    acc[b.balance_type].push(b)
    return acc
  }, {})

  const allErrors = Object.entries(errors)
    .filter(([key]) => key !== "manager_code")
    .map(([, message]) => message)

  return (
    <>
      <Head title="Request Time Off - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/time_off_requests">My Requests</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Request</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/time_off_requests">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">New Request</h1>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Form */}
          <div className="xl:col-span-7">
            <form onSubmit={handleSubmit}>
              <div className="rounded-xl border bg-card">
                <div className="p-6">
                  {allErrors.length > 0 && (
                    <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                      <ul className="space-y-1 text-sm text-destructive">
                        {allErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-5">
                    {/* Type + Unit row */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="request_type">Type</Label>
                        <Select value={data.request_type} onValueChange={(v) => setData("request_type", v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {REQUEST_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="request_type_unit">Unit</Label>
                        <Select value={data.request_type_unit} onValueChange={(v) => setData("request_type_unit", v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {data.request_type_unit === "hours" && (
                      <div className="space-y-2">
                        <Label htmlFor="hours_requested">Hours</Label>
                        <Input
                          id="hours_requested"
                          type="number"
                          step="0.5"
                          min="0.5"
                          placeholder="0"
                          value={data.hours_requested}
                          onChange={(e) => setData("hours_requested", e.target.value as any)}
                        />
                        <p className="text-muted-foreground text-xs">Contract: {contract_hours_per_day}h/day</p>
                      </div>
                    )}

                    <Separator />

                    {/* Date Range */}
                    <div className="space-y-2">
                      <Label>Dates</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <IconCalendar className="mr-2 h-4 w-4" />
                            {data.start_date && data.end_date ? (
                              <>
                                {format(parse(data.start_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                                {" – "}
                                {format(parse(data.end_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                              </>
                            ) : data.start_date ? (
                              <>
                                {format(parse(data.start_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                                {" – Select end date"}
                              </>
                            ) : (
                              <span className="text-muted-foreground">Pick a date range</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="range"
                            numberOfMonths={2}
                            selected={
                              data.start_date
                                ? {
                                    from: parse(data.start_date, "yyyy-MM-dd", new Date()),
                                    to: data.end_date ? parse(data.end_date, "yyyy-MM-dd", new Date()) : undefined,
                                  }
                                : undefined
                            }
                            onSelect={(range: DateRange | undefined) => {
                              setFormData((prev) => ({
                                ...prev,
                                start_date: range?.from ? format(range.from, "yyyy-MM-dd") : "",
                                end_date: range?.to ? format(range.to, "yyyy-MM-dd") : "",
                              }))
                            }}
                            disabled={{ before: new Date() }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Calculation inline */}
                    {calculation && (
                      <div className="bg-muted/50 flex items-center gap-3 rounded-lg px-4 py-3">
                        <IconCalendar className="text-muted-foreground h-4 w-4 shrink-0" />
                        <div className="text-sm">
                          <span className="font-medium">
                            {calculation.business_days} business day{calculation.business_days !== 1 ? "s" : ""}
                          </span>
                          {data.request_type_unit === "hours" && (
                            <span className="text-muted-foreground"> &middot; {calculation.total_hours}h</span>
                          )}
                          {calculation.holidays.length > 0 && (
                            <span className="text-muted-foreground">
                              {" "}
                              &middot; excl. {calculation.holidays.join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Overlapping teammates advisory */}
                    {overlapping.length > 0 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Teammates with overlapping time off
                        </p>
                        <ul className="mt-1.5 space-y-1">
                          {overlapping.map((t, i) => (
                            <li key={i} className="text-sm text-amber-700 dark:text-amber-300">
                              <span className="font-medium">{t.user_name}</span>{" "}
                              <span className="text-amber-600 dark:text-amber-400">
                                {t.request_type} &middot; {formatDate(t.start_date)} – {formatDate(t.end_date)}
                                {t.status === "pending" ? " (pending)" : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {shortNotice && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Manager code required</p>
                        <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                          This request starts in less than {notice_days} days. You'll be asked for a manager code to
                          submit it.
                        </p>
                      </div>
                    )}

                    <Separator />

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label htmlFor="notes">
                        Notes <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <textarea
                        id="notes"
                        rows={3}
                        placeholder="Any additional context..."
                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        value={data.notes}
                        onChange={(e) => setData("notes", e.target.value)}
                      />
                    </div>

                    {/* File Upload */}
                    <div className="space-y-2">
                      <Label>
                        Attachments <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <div
                        className="flex h-24 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed transition-colors hover:bg-muted/50"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById("file-input")?.click()}
                      >
                        <IconUpload className="text-muted-foreground mb-1 h-5 w-5" />
                        <p className="text-muted-foreground text-xs">
                          Drop files or <span className="text-foreground font-medium">browse</span>
                        </p>
                      </div>
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        className="hidden"
                        onChange={handleFileSelect}
                      />

                      {selectedFiles.length > 0 && (
                        <div className="space-y-1.5">
                          {selectedFiles.map((file, i) => (
                            <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <IconFileText className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="truncate text-sm">{file.name}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() => removeFile(i)}
                              >
                                <IconX className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <Separator />
                <div className="flex justify-end gap-2 p-4">
                  <Button variant="ghost" asChild>
                    <a href="/time_off_requests">Cancel</a>
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? "Submitting..." : "Submit Request"}
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Balance Sidebar */}
          <div className="xl:col-span-5">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-4 text-sm font-medium">Your Balance</h3>

              {Object.keys(groupedBalances).length > 0 ? (
                <div className="space-y-5">
                  {Object.entries(groupedBalances).map(([type, typeBalances]) => {
                    const sorted = [...typeBalances].sort((a, b) => a.year - b.year)
                    const totalRemaining = sorted.reduce((s, b) => s + Number(b.remaining_days), 0)
                    const totalAvailable = sorted.reduce((s, b) => s + Number(b.days_available), 0)
                    const usedPct =
                      totalAvailable > 0 ? Math.round(((totalAvailable - totalRemaining) / totalAvailable) * 100) : 0

                    return (
                      <div key={type}>
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-medium capitalize">{type}</span>
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {formatDays(totalRemaining)} / {formatDays(totalAvailable)} days
                          </span>
                        </div>
                        <div className="bg-muted mt-1.5 h-1.5 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-primary h-full rounded-full transition-all"
                            style={{ width: `${Math.min(100, 100 - usedPct)}%` }}
                          />
                        </div>
                        {sorted.length > 1 && (
                          <div className="mt-2 space-y-0.5">
                            {sorted.map((b) => (
                              <div
                                key={b.id}
                                className="text-muted-foreground flex justify-between text-xs tabular-nums"
                              >
                                <span>{b.year}</span>
                                <span>
                                  {formatDays(b.remaining_days)} / {formatDays(b.days_available)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No balance configured. Contact your admin.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manager code required</DialogTitle>
            <DialogDescription>
              This request starts in less than {notice_days} days. Enter the current manager code to submit it. Ask an
              admin for the code.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submitRequest(managerCode)
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="manager_code">Manager code</Label>
              <Input
                id="manager_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={managerCode}
                onChange={(e) => setManagerCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
              />
              {errors.manager_code && <p className="text-sm text-destructive">Manager code {errors.manager_code}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCodeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={processing || managerCode.length !== 6}>
                {processing ? "Submitting..." : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

TimeOffRequestNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
